import { useState } from "react";
import type { FormEvent } from "react";

type Status = "idle" | "submitting" | "success" | "error";

export default function NewsletterSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，感谢你的关注！");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setMessage("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch("https://readdy.ai/api/form/db514e6k6jkc2oct4aug", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      const failed =
        !response.ok ||
        (parsed ? parsed.code !== "OK" : false) ||
        String(serverMsg).toLowerCase().includes("spam");

      if (failed) {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
        return;
      }

      setStatus("success");
      setMessage("订阅成功，感谢你的关注！我们会在每周五为你送上动漫周报。");
      form.reset();
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后再试。");
    }
  };

  return (
    <section id="subscribe" className="border-b border-background-200/70 bg-background-100/60">
      <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-background-200/80 bg-background-50 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* text side */}
          <div className="border-background-200/70 p-6 md:p-10 lg:border-r">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
              <i className="ri-mail-send-line text-sm"></i>
              订阅服务
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              订阅 78 动漫周报
            </h2>
            <p className="mt-4 max-w-md text-sm leading-7 text-foreground-600">
              每周五汇总本周动漫新闻、新番情报与编辑部评测精选，直接送到你的邮箱，不再错过任何一部好番。
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-foreground-700">
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-base text-secondary-500"></i>
                每周一期，绝不刷屏
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-base text-secondary-500"></i>
                新番预告与追番日历
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-base text-secondary-500"></i>
                随时可退订
              </li>
            </ul>
          </div>

          {/* form side */}
          <div className="p-6 md:p-10">
            <form
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label htmlFor="nl-email" className="text-sm font-medium text-foreground-800">
                  邮箱地址
                </label>
                <input
                  id="nl-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="nl-nickname" className="text-sm font-medium text-foreground-800">
                  称呼（选填）
                </label>
                <input
                  id="nl-nickname"
                  name="nickname"
                  type="text"
                  placeholder="怎么称呼你"
                  className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="nl-interest" className="text-sm font-medium text-foreground-800">
                  最关注的内容
                </label>
                <select
                  id="nl-interest"
                  name="interest"
                  defaultValue="新番情报"
                  className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="新番情报">新番情报</option>
                  <option value="作品评测">作品评测</option>
                  <option value="情报速报">情报速报</option>
                  <option value="专题企划">专题企划</option>
                </select>
              </div>

              {/* anti-spam honeypot */}
              <input
                type="text"
                name="website_alt"
                className="form-website-alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-1 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    提交中
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-base"></i>
                    立即订阅
                  </>
                )}
              </button>

              {status !== "idle" && status !== "submitting" && message && (
                <p
                  className={
                    status === "success"
                      ? "flex items-start gap-2 rounded-md bg-secondary-100 px-3.5 py-2.5 text-sm text-secondary-900"
                      : "flex items-start gap-2 rounded-md bg-primary-100 px-3.5 py-2.5 text-sm text-primary-800"
                  }
                >
                  <i
                    className={
                      status === "success"
                        ? "ri-checkbox-circle-line mt-0.5 text-base"
                        : "ri-error-warning-line mt-0.5 text-base"
                    }
                  ></i>
                  <span>{message}</span>
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}